import { useState, type FormEvent } from 'react';
import { api, errorText } from '../../api';
import { Dialog } from '../../components/Dialog';
import { fmtDate } from '../../lib/format';
import { useToast } from '../../lib/toast';
import { useApi } from '../../lib/useApi';
import { EditForm } from './EditForm';
import { entryPill } from './entry';
import type { EmployeeChange, EmployeeDetail, EmployeeTermination } from './types';

// Ausweiskopie nur mit Einverständnis (Sam 12.10.): wer zugestimmt hat und wann, oder dass der Mandant den Ausweis
// persönlich ansieht
function ausweisNote(e: EmployeeDetail): string | null {
  const copy = e.files.some((f) => f.kind.startsWith('ausweis'));
  if (copy && e.ausweisConsent) {
    const by =
      e.ausweisConsent.by === 'mitarbeiter' ? 'des Mitarbeiters über den Link' : 'des Mitarbeiters, bestätigt vom Mandanten';
    return `Ausweiskopie mit Einverständnis ${by} am ${fmtDate(e.ausweisConsent.at)}`;
  }
  if (!copy && e.info.some((r) => r.label === 'Ausweisart')) {
    return 'Keine Ausweiskopie (kein Einverständnis). Den Ausweis sieht der Mandant persönlich an.';
  }
  return null;
}

// Personalakte eines Mitarbeiters: alle Angaben aus dem Personalfragebogen, Ausweis-Dateien und „Erledigt“
// (= angemeldet). Dazu Änderungen und die Kündigung, die der Mandant gemeldet hat, jeweils mit „Erledigt“; bei der
// Kündigung kann die Lohnbuchhaltung die Daten korrigieren. „Angaben ändern“ ändert die Angaben selbst (EditForm).
// Der Mandant bekommt jedes Mal eine Meldung in der App.
export function EmployeeDialog({ id, onClose }: { id: string; onClose(): void }) {
  const toast = useToast();
  const res = useApi<EmployeeDetail>(`/api/kanzlei/employee/${id}`, { live: ['lohn'] });
  const [busy, setBusy] = useState(false);
  const [ask, setAsk] = useState(false);
  const [editing, setEditing] = useState(false);
  const e = res.data;

  // Erledigt setzen; close = danach schließen (sonst bleibt die Akte offen und lädt neu)
  const done = async (url: string, msg: string, close: boolean) => {
    setBusy(true);
    try {
      await api.post(url);
      toast(msg);
      if (close) onClose();
      else {
        await res.reload();
        setBusy(false);
      }
    } catch (err) {
      toast(errorText(err));
      setBusy(false);
    }
  };

  // Nie angemeldeten Mitarbeiter löschen (z. B. doppelt gemeldet): endgültig mit allen Dateien
  const remove = async (name: string) => {
    setBusy(true);
    try {
      await api.del(`/api/kanzlei/employee/${id}`);
      toast(`${name} gelöscht`);
      onClose();
    } catch (err) {
      toast(errorText(err));
      setBusy(false);
      setAsk(false);
    }
  };

  const pill = e && entryPill(e.entryDate, e.status !== 'neu');
  const t = e?.termination;
  return (
    <Dialog open onClose={onClose} title={e?.name ?? 'Mitarbeiter'} className="emp">
      {!e ? (
        res.error ? (
          <p className="note">{res.error}</p>
        ) : (
          <div className="spinner" aria-label="Lädt" />
        )
      ) : (
        <>
          <p className="note">
            {e.clientName} · gemeldet am {fmtDate(e.createdAt)}
            {e.createdBy ? ` von ${e.createdBy}` : ''}
          </p>
          <p className="emp-st">
            {e.status === 'neu' ? (
              <span className="pill p-info">neu gemeldet</span>
            ) : (
              <span className="pill p-ok">
                angemeldet{e.registeredAt ? ` am ${fmtDate(e.registeredAt)}` : ''}
                {e.registeredBy ? ` von ${e.registeredBy}` : ''}
              </span>
            )}{' '}
            {pill && <span className={`pill p-${pill.tone}`}>{pill.label}</span>} {t && <TerminationPill t={t} />}
          </p>
          {e.status === 'neu' &&
            (e.rehiredFrom ? (
              <div className="lock">
                Wiedereinstellung: vorher beschäftigt vom {fmtDate(e.rehiredFrom.entryDate)}
                {e.rehiredFrom.lastDay ? ` bis ${fmtDate(e.rehiredFrom.lastDay)}` : ''}. Zu tun: Anmeldung zur Sozialversicherung,
                Lohnkonto wieder aufnehmen oder neu anlegen, ELStAM abrufen
              </div>
            ) : (
              <div className="lock">Zu tun: Anmeldung zur Sozialversicherung, Lohnkonto anlegen, ELStAM abrufen</div>
            ))}
          {e.rehiredAs && (
            <p className="note">
              Wieder eingestellt zum {fmtDate(e.rehiredAs.entryDate)}
              {e.rehiredAs.status === 'neu' ? ' (neu gemeldet, noch nicht angemeldet)' : ''}
            </p>
          )}
          {t && <TerminationBox e={e} t={t} reload={res.reload} />}
          {e.changes.length > 0 && (
            <section className="emp-box">
              <h3>Änderungen vom Mandanten</h3>
              {[...e.changes.filter((c) => c.status === 'offen'), ...e.changes.filter((c) => c.status !== 'offen').reverse()].map(
                (c) => (
                  <ChangeRow
                    key={c.id}
                    c={c}
                    busy={busy}
                    onDone={() =>
                      done(
                        `/api/kanzlei/employee-change/${c.id}/done`,
                        `Änderung übernommen · ${e.contactName} sieht es in der App`,
                        false,
                      )
                    }
                  />
                ),
              )}
            </section>
          )}
          {e.files.length > 0 && (
            <div className="emp-files">
              {e.files.map((f) => (
                <a
                  key={f.kind}
                  className="btn sm"
                  href={`/api/kanzlei/employee/${e.id}/file/${f.kind}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  📄 {f.label}
                </a>
              ))}
            </div>
          )}
          {ausweisNote(e) && <p className="note">{ausweisNote(e)}</p>}
          <div className="emp-h">
            <h3>Angaben</h3>
            {!editing && (
              <button type="button" className="btn sm" onClick={() => setEditing(true)}>
                Angaben ändern
              </button>
            )}
          </div>
          {editing ? (
            <EditForm
              e={e}
              onCancel={() => setEditing(false)}
              onSaved={async () => {
                setEditing(false);
                await res.reload();
              }}
            />
          ) : (
            <>
              <dl className="dl emp-dl">
                {e.info.map((r) => (
                  <div key={r.label} style={{ display: 'contents' }}>
                    <dt>{r.label}</dt>
                    <dd>
                      {r.value}
                      {r.note && <small>{r.note}</small>}
                    </dd>
                  </div>
                ))}
              </dl>
              <div className="dlg-actions">
                {ask && e.status === 'neu' ? (
                  <div className="emp-del">
                    <span>
                      {e.name} endgültig löschen? Angaben und Dateien sind dann weg, auch für {e.contactName}. Das geht nur,
                      solange er nicht angemeldet ist.
                    </span>
                    <span className="bk-actions">
                      <button type="button" className="btn sm" onClick={() => setAsk(false)}>
                        Abbrechen
                      </button>
                      <button type="button" className="btn sm dark" disabled={busy} onClick={() => remove(e.name)}>
                        Ja, löschen
                      </button>
                    </span>
                  </div>
                ) : (
                  <>
                    {e.status === 'neu' && (
                      <button type="button" className="btn emp-del-btn" disabled={busy} onClick={() => setAsk(true)}>
                        Löschen
                      </button>
                    )}
                    <button type="button" className="btn" onClick={onClose}>
                      Schließen
                    </button>
                    {e.status === 'neu' && (
                      <button
                        type="button"
                        className="btn dark"
                        disabled={busy}
                        onClick={() =>
                          done(
                            `/api/kanzlei/employee/${e.id}/done`,
                            `${e.name} ist angemeldet · ${e.contactName} sieht es in der App`,
                            true,
                          )
                        }
                      >
                        Erledigt: angemeldet
                      </button>
                    )}
                    {t?.status === 'offen' && (
                      <button
                        type="button"
                        className="btn dark"
                        disabled={busy}
                        onClick={() =>
                          done(
                            `/api/kanzlei/termination/${t.id}/done`,
                            `${e.name} ist abgemeldet zum ${fmtDate(t.lastDay)} · ${e.contactName} sieht es in der App`,
                            true,
                          )
                        }
                      >
                        Erledigt: abgemeldet
                      </button>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </>
      )}
    </Dialog>
  );
}

function TerminationPill({ t }: { t: EmployeeTermination }) {
  const last = fmtDate(t.lastDay);
  if (t.status === 'offen') {
    return <span className={`pill ${t.gone ? 'p-bad' : 'p-warn'}`}>Kündigung gemeldet · letzter Arbeitstag {last}</span>;
  }
  return <span className="pill p-neu">abgemeldet zum {last}</span>;
}

function ChangeRow({ c, busy, onDone }: { c: EmployeeChange; busy: boolean; onDone(): void }) {
  return (
    <div className="emp-chg">
      <div className="tx">
        <b>
          {c.what}: {c.value}
        </b>
        <small>
          {c.validFrom ? `gültig ab ${fmtDate(c.validFrom)}` : 'ab der nächsten Abrechnung'} · gemeldet am {fmtDate(c.createdAt)}
          {c.createdBy ? ` von ${c.createdBy}` : ''}
          {c.doneAt && ` · übernommen am ${fmtDate(c.doneAt)}${c.doneBy ? ` von ${c.doneBy}` : ''}`}
        </small>
      </div>
      {c.file && (
        <a className="btn sm" href={`/api/kanzlei/employee-change/${c.id}/file`} target="_blank" rel="noreferrer">
          📄 Nachweis
        </a>
      )}
      {c.status === 'offen' ? (
        <button type="button" className="btn sm dark" disabled={busy} onClick={onDone}>
          Erledigt: übernommen
        </button>
      ) : (
        <span className="pill p-ok">übernommen</span>
      )}
    </div>
  );
}

// Kündigung mit allen Angaben des Mandanten, Kündigungsschreiben und „Datum korrigieren“
function TerminationBox({ e, t, reload }: { e: EmployeeDetail; t: EmployeeTermination; reload(): Promise<void> }) {
  const [edit, setEdit] = useState(false);
  const [storno, setStorno] = useState(false);
  const aufhebung = t.by === 'Aufhebungsvertrag';
  return (
    <section className="emp-box">
      <h3>Kündigung</h3>
      <dl className="dl">
        <dt>Wer hat gekündigt</dt>
        <dd>{t.by}</dd>
        {t.receivedOn && (
          <>
            <dt>{aufhebung ? 'Vertrag unterschrieben am' : 'Zugegangen am'}</dt>
            <dd>{fmtDate(t.receivedOn)}</dd>
          </>
        )}
        <dt>Letzter Arbeitstag</dt>
        <dd>
          {fmtDate(t.lastDay)}
          {t.lastDayOld && <small>korrigiert, vorher {fmtDate(t.lastDayOld)}</small>}
        </dd>
        {t.reason && (
          <>
            <dt>Grund der Korrektur</dt>
            <dd>
              {t.reason}
              {t.correctedAt && <small>am {fmtDate(t.correctedAt)}</small>}
            </dd>
          </>
        )}
        {t.vacationDays !== null && (
          <>
            <dt>Resturlaub</dt>
            <dd>
              {String(t.vacationDays).replace('.', ',')} {t.vacationDays === 1 ? 'Tag' : 'Tage'}
            </dd>
          </>
        )}
        {t.note && (
          <>
            <dt>Hinweis des Mandanten</dt>
            <dd>{t.note}</dd>
          </>
        )}
        <dt>Gemeldet</dt>
        <dd>
          am {fmtDate(t.createdAt)}
          {t.createdBy ? ` von ${t.createdBy}` : ''}
        </dd>
        {t.doneAt && (
          <>
            <dt>Erledigt</dt>
            <dd>
              am {fmtDate(t.doneAt)}
              {t.doneBy ? ` von ${t.doneBy}` : ''}
            </dd>
          </>
        )}
      </dl>
      {t.status === 'offen' && (
        <div className="lock">Zu tun: Abmeldung zur Sozialversicherung, letzte Lohnabrechnung, Arbeitsbescheinigung</div>
      )}
      {edit ? (
        <CorrectForm
          e={e}
          t={t}
          onCancel={() => setEdit(false)}
          onDone={async () => {
            setEdit(false);
            await reload();
          }}
        />
      ) : storno ? (
        <StornoForm e={e} t={t} onCancel={() => setStorno(false)} onDone={reload} />
      ) : (
        <div className="bk-actions">
          {t.file && (
            <a className="btn sm" href={`/api/kanzlei/termination/${t.id}/file`} target="_blank" rel="noreferrer">
              📄 Kündigungsschreiben
            </a>
          )}
          <button type="button" className="btn sm" onClick={() => setEdit(true)}>
            Datum korrigieren
          </button>
          {/* wieder eingestellt: die neue Personalakte gilt, die alte bleibt abgemeldet */}
          {!e.rehiredAs && (
            <button type="button" className="btn sm" onClick={() => setStorno(true)}>
              Kündigung stornieren
            </button>
          )}
        </div>
      )}
    </section>
  );
}

// „Kündigung stornieren“ (Sam 12.10.): Der Mitarbeiter bleibt angemeldet, der Mandant bekommt eine Meldung. War er schon
// abgemeldet, muss die Abmeldung im Lohnprogramm storniert werden.
function StornoForm({
  e,
  t,
  onDone,
  onCancel,
}: {
  e: EmployeeDetail;
  t: EmployeeTermination;
  onDone(): Promise<void>;
  onCancel(): void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      await api.post(`/api/kanzlei/termination/${t.id}/cancel`);
      toast(`Kündigung storniert · ${e.contactName} sieht es in der App`);
      await onDone();
    } catch (err) {
      toast(errorText(err));
      setBusy(false);
    }
  };
  return (
    <div className="emp-corr" role="alertdialog" aria-label="Kündigung stornieren">
      <p className="note">
        Kündigung von {e.name} stornieren? {e.name} bleibt angemeldet, {e.contactName} bekommt eine Meldung in der App.
        {t.file && ' Das Kündigungsschreiben wird gelöscht.'}
      </p>
      {t.status === 'erledigt' && (
        <div className="lock">
          Zu tun danach: Abmeldung zur Sozialversicherung im Lohnprogramm stornieren, Lohnkonto weiterführen
        </div>
      )}
      <div className="bk-actions">
        <button type="button" className="btn sm dark" disabled={busy} onClick={go}>
          Ja, stornieren
        </button>
        <button type="button" className="btn sm" disabled={busy} onClick={onCancel}>
          Abbrechen
        </button>
      </div>
    </div>
  );
}

function CorrectForm({
  e,
  t,
  onDone,
  onCancel,
}: {
  e: EmployeeDetail;
  t: EmployeeTermination;
  onDone(): void;
  onCancel(): void;
}) {
  const toast = useToast();
  const befristung = t.by === 'Befristung läuft aus';
  const [receivedOn, setReceivedOn] = useState(t.receivedOn ?? '');
  const [lastDay, setLastDay] = useState(t.lastDay);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const save = async (ev: FormEvent) => {
    ev.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.post(`/api/kanzlei/termination/${t.id}/correct`, {
        receivedOn: befristung ? null : receivedOn || null,
        lastDay,
        reason,
      });
      toast(`Daten korrigiert · ${e.contactName} sieht es in der App`);
      onDone();
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };

  return (
    <form className="form emp-corr" onSubmit={save} noValidate>
      <div className="row">
        {!befristung && (
          <label>
            {t.by === 'Aufhebungsvertrag' ? 'Vertrag unterschrieben am' : 'Zugegangen am'}
            <input type="date" value={receivedOn} max="2100-12-31" onChange={(x) => setReceivedOn(x.target.value)} />
          </label>
        )}
        <label>
          Letzter Arbeitstag
          <input
            type="date"
            value={lastDay}
            min={e.entryDate}
            max="2100-12-31"
            required
            onChange={(x) => setLastDay(x.target.value)}
          />
        </label>
      </div>
      <label>
        Grund (sieht der Mandant)
        <input
          value={reason}
          maxLength={200}
          required
          placeholder="z. B. Kündigungsfrist nachgerechnet"
          onChange={(x) => setReason(x.target.value)}
        />
      </label>
      {error && (
        <p className="note emp-err" role="alert">
          {error}
        </p>
      )}
      <div className="bk-actions">
        <button type="submit" className="btn sm dark" disabled={busy}>
          Speichern
        </button>
        <button type="button" className="btn sm" onClick={onCancel}>
          Abbrechen
        </button>
      </div>
    </form>
  );
}
